/**
 * Lightweight User-Agent parser (no npm dependency) + viewport-based
 * device classification. Shared between server (quote/contact form
 * submissions, parsing a UA string passed via formData) and client
 * (client-logger, parsing navigator.userAgent directly).
 */

export interface ParsedUserAgent {
	browser: string;
	os: string;
}

export function parseUserAgent(ua: string): ParsedUserAgent {
	// Browser detection (order matters — check specific before generic)
	let browser = 'Onbekend';
	if (/CriOS\/([\d.]+)/.test(ua)) {
		browser = `Chrome iOS ${RegExp.$1.split('.')[0]}`;
	} else if (/FxiOS\/([\d.]+)/.test(ua)) {
		browser = `Firefox iOS ${RegExp.$1.split('.')[0]}`;
	} else if (/EdgA?\/([\d.]+)/.test(ua)) {
		browser = `Edge ${RegExp.$1.split('.')[0]}`;
	} else if (/OPR\/([\d.]+)/.test(ua)) {
		browser = `Opera ${RegExp.$1.split('.')[0]}`;
	} else if (/SamsungBrowser\/([\d.]+)/.test(ua)) {
		browser = `Samsung Browser ${RegExp.$1.split('.')[0]}`;
	} else if (/Chrome\/([\d.]+)/.test(ua) && !/Chromium/.test(ua)) {
		browser = `Chrome ${RegExp.$1.split('.')[0]}`;
	} else if (/Version\/([\d.]+).*Safari/.test(ua)) {
		browser = `Safari ${RegExp.$1.split('.')[0]}`;
	} else if (/Firefox\/([\d.]+)/.test(ua)) {
		browser = `Firefox ${RegExp.$1.split('.')[0]}`;
	}

	// OS detection
	let os = 'Onbekend';
	if (/iPhone OS ([\d_]+)/.test(ua)) {
		os = `iOS ${RegExp.$1.replace(/_/g, '.')}`;
	} else if (/iPad.*OS ([\d_]+)/.test(ua)) {
		os = `iPadOS ${RegExp.$1.replace(/_/g, '.')}`;
	} else if (/Android ([\d.]+)/.test(ua)) {
		os = `Android ${RegExp.$1}`;
	} else if (/Mac OS X ([\d_.]+)/.test(ua)) {
		os = `macOS ${RegExp.$1.replace(/_/g, '.')}`;
	} else if (/Windows NT ([\d.]+)/.test(ua)) {
		const winVersions: Record<string, string> = {
			'10.0': 'Windows 10/11',
			'6.3': 'Windows 8.1',
			'6.1': 'Windows 7',
		};
		os = winVersions[RegExp.$1] || `Windows NT ${RegExp.$1}`;
	} else if (/Linux/.test(ua)) {
		os = 'Linux';
	}

	return { browser, os };
}

export type DeviceType = 'mobile' | 'tablet' | 'desktop';

export function classifyDeviceType(width: number): DeviceType {
	if (width < 768) return 'mobile';
	if (width < 1024) return 'tablet';

	return 'desktop';
}
